import { useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"

// Keep in sync with Image::MAX_FILE_SIZE, which is the real rule. This just
// saves waiting for a large file to upload before hearing it's too big.
const MAX_FILE_BYTES = 10 * 1024 * 1024
const FILE_TOO_LARGE = "File must be 10 MB or smaller"

type ImageFormProps = {
  id: number | null
  caption: string
  alt_text: string
  credit: string
  credit_url: string
  has_file?: boolean
}

type PreAttach = { type: string; id: number; name: string }

type PageProps = {
  image: ImageFormProps
  pre_attach?: PreAttach | null
  return_to?: string | null
  errors?: Record<string, string>
}

export default function AdminImageForm({ image, pre_attach, return_to }: { image: ImageFormProps; pre_attach?: PreAttach | null; return_to?: string | null }) {
  const { errors = {} } = usePage<PageProps>().props
  const isNew = image.id === null
  const [form, setForm] = useState({
    caption: image.caption ?? "",
    alt_text: image.alt_text ?? "",
    credit: image.credit ?? "",
    credit_url: image.credit_url ?? "",
  })
  const [file, setFile] = useState<File | null>(null)
  const fileTooLarge = file !== null && file.size > MAX_FILE_BYTES

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (fileTooLarge) return
    const formData = new FormData()
    formData.append("image[caption]", form.caption)
    formData.append("image[alt_text]", form.alt_text)
    formData.append("image[credit]", form.credit)
    formData.append("image[credit_url]", form.credit_url)
    if (file) formData.append("image[file]", file)
    if (isNew && pre_attach) {
      formData.append("pre_attach_type", pre_attach.type)
      formData.append("pre_attach_id", String(pre_attach.id))
    }
    if (isNew && return_to) {
      formData.append("return_to", return_to)
    }
    if (isNew) {
      router.post("/admin/images", formData)
    } else {
      router.patch(`/admin/images/${image.id}`, formData)
    }
  }

  const title = isNew ? "New image" : `Edit image #${image.id}`

  return (
    <>
      <Head title={title}>
        <meta name="description" content={`${title} in the admin area.`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={`${title} in the admin area.`} />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <h1>{title}</h1>
          {isNew && pre_attach && (
            <p className="mt-1 text-sm text-ink-muted">
              Will be attached to <strong className="text-ink-body">{pre_attach.type}: {pre_attach.name}</strong>
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="mt-6 max-w-xl space-y-4">
          <div>
            <label htmlFor="caption" className="block text-sm text-ink-muted mb-1">Caption</label>
            <input
              id="caption"
              name="caption"
              type="text"
              value={form.caption}
              onChange={handleChange}
              className="w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"
            />
            {errors.caption && <p className="mt-1 text-xs text-red-600">{errors.caption}</p>}
          </div>

          <div>
            <label htmlFor="alt_text" className="block text-sm text-ink-muted mb-1">Alt text</label>
            <input
              id="alt_text"
              name="alt_text"
              type="text"
              value={form.alt_text}
              onChange={handleChange}
              className="w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"
            />
            {errors.alt_text && <p className="mt-1 text-xs text-red-600">{errors.alt_text}</p>}
          </div>

          <div>
            <label htmlFor="credit" className="block text-sm text-ink-muted mb-1">Credit</label>
            <input
              id="credit"
              name="credit"
              type="text"
              placeholder="e.g. Photo courtesy of Jane Smith"
              value={form.credit}
              onChange={handleChange}
              className="w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"
            />
            {errors.credit && <p className="mt-1 text-xs text-red-600">{errors.credit}</p>}
          </div>

          <div>
            <label htmlFor="credit_url" className="block text-sm text-ink-muted mb-1">Credit link <span className="font-normal">(optional)</span></label>
            <input
              id="credit_url"
              name="credit_url"
              type="url"
              placeholder="https://…"
              value={form.credit_url}
              onChange={handleChange}
              className="w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"
            />
            {errors.credit_url && <p className="mt-1 text-xs text-red-600">{errors.credit_url}</p>}
          </div>

          <div>
            <p className="block text-sm text-ink-muted mb-1">
              Image file{image.has_file && " (replace existing)"}
            </p>
            <div className="flex items-center gap-3">
              <label className="inline-flex cursor-pointer items-center rounded-md border border-hairline bg-surface px-3 py-1.5 text-sm font-medium text-ink-body hover:bg-page transition-colors">
                Choose file
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  className="sr-only"
                />
              </label>
              <span className="text-sm text-ink-muted truncate">
                {file ? file.name : "No file chosen"}
              </span>
            </div>
            <p className="mt-1 text-xs text-ink-muted">Maximum size 10 MB.</p>
            {fileTooLarge
              ? <p className="mt-1 text-xs text-red-600">{FILE_TOO_LARGE}</p>
              : errors.file && <p className="mt-1 text-xs text-red-600">{errors.file}</p>}
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button type="submit">{isNew ? "Create image" : "Update image"}</Button>
            <Link
              href={isNew ? "/admin/images" : `/admin/images/${image.id}`}
              className="text-sm text-ink-muted no-underline hover:text-ink-display"
            >
              Cancel
            </Link>
          </div>
        </form>
      </AdminShell>
    </>
  )
}
